/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { StreamTimelineManager } from '../StreamTimelineManager';
import { StreamTimelineConfiguration } from '../model/StreamTimelineConfiguration';
import { EventStore } from '../EventStore';
import { EventInfo } from '../model/EventInfo';
import { Player } from '@quickplay/player-interface';
import { Logger, LoggerLevel, createLogger, Error } from '@quickplay/foundation';

/**
 * The default events collection name for this stream timeline manager.
 */
const EVENTS_COLLECTION_NAME = 'plays';
const TAG_NAME = 'FL_STREAM_TIMELINE';

export class DefaultStreamTimelineManager implements StreamTimelineManager {
  private isSubscribed = false;
  private logger_: Logger;
  constructor(
    private streamMetadataConfiguration: StreamTimelineConfiguration,
    private eventStore: EventStore,
    logger?: Logger,
  ) {
    this.logger_ = logger || createLogger(LoggerLevel.OFF, TAG_NAME);
  }

  get configuration(): StreamTimelineConfiguration {
    return this.streamMetadataConfiguration;
  }

  get gameInfo(): EventInfo | undefined {
    return this.eventStore.gameInfo;
  }

  private async subscribeToEventStore(): Promise<Error | undefined> {
    this.isSubscribed = true;
    // subscribe to realtime updates on league/game Firebase document
    const subscribeToGameMetadata = await this.eventStore.subscribeToGameMetadata(
      this.streamMetadataConfiguration.leagueName,
      this.streamMetadataConfiguration.gameId,
    );

    if (subscribeToGameMetadata !== undefined) {
      return subscribeToGameMetadata;
    }
    // subscribe to realtime updates on league/game/EVENTS_COLLECTION_NAME Firebase collection
    return this.eventStore.subscribeToEvents(
      this.streamMetadataConfiguration.leagueName,
      this.streamMetadataConfiguration.gameId,
      EVENTS_COLLECTION_NAME,
    );
  }

  setup(): Promise<Error | undefined> {
    if (this.isSubscribed) {
      this.logger_.warn('StreamTimelineManager is already setup. Ignoring duplicate setup.');
      return Promise.resolve(undefined);
    }
    return this.subscribeToEventStore();
  }

  exists(): Promise<boolean> {
    return this.eventStore.exists(this.streamMetadataConfiguration.leagueName, this.streamMetadataConfiguration.gameId);
  }

  shutdown(): void {
    if (this.eventStore) {
      this.eventStore.unsubscribeFromGameMetadata();
      this.eventStore.unsubscribeFromEvents();
      this.isSubscribed = false;
    }
  }

  updatePlayer(player: Player): void {
    this.eventStore.updatePlayer(player);
  }
}
